import { api } from "utils/api";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { ArtistReview, ArtistReviewFormData } from "../types";

const createArtistReview = async (
  artistId: string,
  { isAnonymous, ...formData }: ArtistReviewFormData
) =>
  api.post<ArtistReview>(`/artists/${artistId}/reviews`, {
    body: JSON.stringify({ ...formData, isAnonymous: isAnonymous === "true" }),
    requireAuth: true,
  });

const useCreateArtistReview = () =>
  useMutation(
    ({
      artistId,
      formData,
    }: {
      artistId: string;
      formData: ArtistReviewFormData;
    }) => createArtistReview(artistId, formData)
  );

const updateArtistReview = async (
  artistId: string,
  reviewId: string,
  { isAnonymous, ...formData }: ArtistReviewFormData
) =>
  api.put<ArtistReview>(`/artists/${artistId}/reviews/${reviewId}`, {
    body: JSON.stringify({ ...formData, isAnonymous: isAnonymous === "true" }),
    requireAuth: true,
  });

const useUpdateArtistReview = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      artistId,
      reviewId,
      formData,
    }: {
      artistId: string;
      reviewId: string;
      formData: ArtistReviewFormData;
    }) => updateArtistReview(artistId, reviewId, formData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["reviews", "ARTIST", data.reviewableId]);
      },
    }
  );
};

type WatchedTagValues = string[][];

/**
 * Transform a into expected shape for form fields
 */
const transformArtistReviewToFields = ({
  isAnonymous,
  ...rest
}: ArtistReview): ArtistReviewFormData => ({
  ...rest,
  isAnonymous: isAnonymous ? "true" : "false",
});

const useArtistReviewForm = (artistId: string, review?: ArtistReview) => {
  const { handleSubmit, watch, ...rest } = useForm<ArtistReviewFormData>({
    defaultValues: review
      ? transformArtistReviewToFields(review)
      : {
          roadmapTags: [],
          artTags: [],
          communityTags: [],
          founderTeamTags: [],
          isAnonymous: "false",
          status: "PENDING",
        },
  });

  const { mutate: create, ...createMutation } = useCreateArtistReview();
  const { mutate: update, ...updateMutation } = useUpdateArtistReview();

  const isEdit = !!review;
  const mutation = isEdit ? updateMutation : createMutation;

  // Create form submit handler that can handle either update or create mutate calls
  const onSubmit = handleSubmit((formData) =>
    isEdit
      ? update({ artistId, reviewId: review.type, formData })
      : create({ artistId, formData })
  );

  // Watch review tag checkbox values to disable when limit is reached
  const [
    artTagValues,
    communityTagValues,
    founderTeamTagValues,
    roadmapTagValues,
  ] = watch([
    "artTags",
    "communityTags",
    "founderTeamTags",
    "roadmapTags",
  ]) as WatchedTagValues;

  const forYouIf = watch("forYouIf");

  return {
    onSubmit,
    watching: {
      artTagValues,
      communityTagValues,
      founderTeamTagValues,
      roadmapTagValues,
      forYouIf,
    },
    mutation,
    ...rest,
  };
};

export { useArtistReviewForm };
